import type { PricingOptionIndex } from '../../types';

import { parseIsoDateUtc, weekdayOf } from './isoDates';
import { isCompleteSurchargePeriod } from './surchargePeriodValidation';
import type {
  SavedSurchargeSession,
  SurchargeCollectedBy,
  SurchargePeriod,
  SurchargePeriodFormValues,
  SurchargeSessionState,
  Weekday,
} from './types';

/** Monday-first, matching Salesforce's `Day_of_the_Week__c` ordering. */
export const WEEKDAYS: ReadonlyArray<Weekday> = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

export const WEEKDAY_LABELS: Record<Weekday, string> = {
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
  sun: 'Sun',
};

export const SURCHARGE_COLLECTED_BY_OPTIONS: ReadonlyArray<SurchargeCollectedBy> = [
  'Paid at Booking Stage',
  'Paid directly to the Hotel',
];

export const ALL_PRICING_OPTIONS: ReadonlyArray<PricingOptionIndex> = [0, 1, 2];

// What a new period starts with on the tab, so a period from elsewhere without these fields reads the same.
export const NEW_PERIOD_DEFAULTS: Pick<
  SurchargePeriodFormValues,
  'weekdays' | 'collectedBy' | 'accumulates' | 'overlapping' | 'rooms' | 'options'
> = {
  weekdays: [],
  collectedBy: 'Paid at Booking Stage',
  accumulates: true,
  overlapping: false,
  rooms: 'all',
  options: ALL_PRICING_OPTIONS,
};

const MS_PER_DAY = 86_400_000;

export function createEmptySurchargeSession(): SurchargeSessionState {
  return { grids: { periods: [] } };
}

// A session saved while the tab had weekday grids keeps only its periods, so the next save drops the rest.
export function withPeriodsOnly(session: SurchargeSessionState): SurchargeSessionState {
  return { grids: { periods: session.grids.periods } };
}

// Save is refused while any period is incomplete, so the filter only narrows the type.
export function completeSurchargeSession(session: SurchargeSessionState): SavedSurchargeSession {
  return { grids: { periods: session.grids.periods.filter(isCompleteSurchargePeriod) } };
}

export function periodCostAud(
  period: Pick<SurchargePeriod, 'cost' | 'currency'>,
  audRateFor: (currency: string) => number | null,
): number | null {
  if (period.cost == null || !Number.isFinite(period.cost)) {
    return null;
  }
  const rate = audRateFor(period.currency);
  return rate && rate > 0 ? period.cost / rate : null;
}

/**
 * Days a period actually surcharges. With no weekdays picked this is the doc's `to - from + 1`;
 * with weekdays picked it counts only the matching nights, which is the figure the guest pays on.
 */
export function daysSurchargedIn(period: SurchargePeriod): number | null {
  const start = parseIsoDateUtc(period.startDate);
  const end = parseIsoDateUtc(period.endDate);
  if (start == null || end == null || start > end) {
    return null;
  }

  const totalDays = Math.round((end.getTime() - start.getTime()) / MS_PER_DAY) + 1;
  const picked = new Set(period.weekdays);
  if (picked.size === 0) {
    return totalDays;
  }

  // Counted in closed form rather than walked day by day, because a date input accepts years up to
  // 275760 and this runs in render for every period on screen.
  let days = picked.size * Math.floor(totalDays / 7);
  for (let offset = 0; offset < totalDays % 7; offset++) {
    const weekday = weekdayOf(new Date(start.getTime() + offset * MS_PER_DAY).toISOString().slice(0, 10));
    if (weekday != null && picked.has(weekday)) {
      days++;
    }
  }
  return days;
}
